import { useRef, useState } from 'react';
import { conditionalJsonPatch } from '../shared/json-patch.ts';
import { applyObjectPatches, objectPatches } from '../shared/object-patches.ts';
import { atPath } from '../shared/translations.ts';

export function useDraft<T extends object>(saved: T, versionId: string | number) {
  const [state, setState] = useState({
    saved,
    draft: structuredClone(saved),
    versionId,
    receivedVersionId: versionId,
  });
  const submitted = useRef<T | null>(null);
  if (versionId !== state.receivedVersionId) {
    const patches = objectPatches(state.saved, state.draft);
    const conflict = patches.some(
      (patch) =>
        JSON.stringify(atPath(saved, patch.path)) !== JSON.stringify(atPath(state.saved, patch.path)),
    );
    if (!conflict)
      setState({ saved, draft: applyObjectPatches(saved, patches), versionId, receivedVersionId: versionId });
  }
  return {
    draft: state.draft,
    versionId: state.versionId,
    setDraft: (draft: T | ((previous: T) => T)) =>
      setState((previous) => ({
        ...previous,
        draft:
          typeof draft === 'function'
            ? draft(previous.draft)
            : applyObjectPatches(previous.draft, objectPatches(state.draft, draft)),
      })),
    changed: [...new Set(objectPatches(state.saved, state.draft).map((patch) => patch.path[0] as keyof T))],
    captureJsonPatch: (value: T = state.draft) => {
      // Retain the raw editor snapshot so normalization is not mistaken for an edit made during the save.
      submitted.current = structuredClone(state.draft);
      return conditionalJsonPatch(state.saved, objectPatches(state.saved, value), String(state.versionId));
    },
    reset: (value: T, nextVersionId: string | number) => {
      const pending = submitted.current;
      submitted.current = null;
      setState((current) => ({
        receivedVersionId: current.receivedVersionId,
        saved: value,
        draft: pending
          ? applyObjectPatches(value, objectPatches(pending, current.draft))
          : structuredClone(value),
        versionId: nextVersionId,
      }));
    },
  };
}
